import { useCallback } from "react"
import { batch, useDispatch, useSelector } from "react-redux"
import { useLocalization } from "@tm/localization"
import {
    Button,
    Loader,
    ScrollContainer,
    SimpleCard,
    Stack,
    styled,
    Typography,
    Image,
    Icon,
    cardClasses,
    Box,
    useTheme,
    Divider,
} from "@tm/components"
import { RimDetailsArticle, SizeInfo } from "@bundles/wheels/data/model"
import { useDisplayListV2 } from "@tm/context-distribution"
import { MainState } from "../../main"
import { Actions } from "../business"
import RimArticle from "./rimArticle"
import { RimArticleListV2 } from "./rimArticleListV2"
import { StatusPanel } from "./statusPanel"

const StyledSimpleCard = styled(SimpleCard)(({ theme }) => ({
    [`.${cardClasses.root}`]: {
        marginBottom: theme.margin?.s,
        ".error-image": {
            height: "unset",
        },
    },
}))

const StyledMainImage = styled(Image)({
    width: "8em",
    height: "8em",
    margin: "1em",
    alignSelf: "center",
})

const UppercaseLabel = styled(Typography)({
    textTransform: "uppercase",
})

type WheelsDetailsComponentProps = {
    repairTimesRoute: string
}

export function RimDetailsHeaderV2({ repairTimesRoute }: WheelsDetailsComponentProps) {
    const dispatch = useDispatch()
    const theme = useTheme()
    const { translateText } = useLocalization()
    const displayListV2 = useDisplayListV2()

    const { rimItem, selectedSize, loading, data, error, selectedRimDetailsArticle } = useSelector((s: MainState) => ({
        rimItem: s.wheelsDetails.rimItem,
        selectedSize: s.wheelsDetails.selectedSize,
        rimInfoTireSizesResponse: s.wheelsDetails.rimInfoTireSizesResponse,
        isConti: s.wheelsDetails.rimItem?.isConti,
        loading: s.wheelsDetails.rimDetailsArticlesResponse.loading,
        data: s.wheelsDetails.rimDetailsArticlesResponse.data,
        error: s.wheelsDetails.rimDetailsArticlesResponse.error,
        selectedRimDetailsArticle: s.wheelsDetails.selectedRimDetailsArticle,
        registrationNos: s.vehicleSelection,
        selectedTireSize: s.wheelsDetails.selectedTireSize,
    }))

    const renderRimSize = (item: SizeInfo, idx: number) => {
        const handleRimSizeSelect = () => {
            dispatch(Actions.selectRimDetailsSize(item.size))
        }

        return (
            <Button
                size="large"
                key={idx}
                color={selectedSize === item.size ? "highlight" : "secondary"}
                sx={{ marginRight: ".2em" }}
                onClick={handleRimSizeSelect}
            >
                {item.size}
            </Button>
        )
    }

    const handleSelectItem = useCallback((rim: RimDetailsArticle) => {
        batch(() => {
            dispatch(Actions.selectRimDetailsArticle(rim))
            dispatch(Actions.loadRimDetailsAndRestrictions(rim))
            dispatch(Actions.selectTireSize(undefined))
        })
    }, [])

    const handleRimQuantityChange = useCallback(
        (article: RimDetailsArticle, value: number) => {
            dispatch(Actions.changeRimDetailsArticleQuantity(article, value))
        },
        [rimItem?.idRimDesign]
    )

    const renderAxleCombinationArticles = () => {
        if (loading) {
            return (
                <StatusPanel>
                    <Loader />
                </StatusPanel>
            )
        }

        if (!data || !Object.keys(data).length || error) {
            return (
                <StatusPanel>
                    <Typography>{translateText(323)}</Typography>
                </StatusPanel>
            )
        }

        const renderAxleCombinationArticle = ([group, articles]: [string, RimDetailsArticle[]], idx: number) => {
            const uniqueRimSizes = articles.map((x) => x.rimSize).distinct()
            return (
                <Box className="article-list" key={idx}>
                    <Stack direction="row" gap={0.5} mb={0.5}>
                        {uniqueRimSizes.map((x) => (
                            <Button size="large" key={group + x} color="secondary" className="axle-group">
                                {x}
                            </Button>
                        ))}
                    </Stack>
                    {articles.map((x, artIDx) => (
                        <RimArticle
                            key={artIDx + x.applicationId || x.eanCode}
                            item={x}
                            repairTimesRoute={repairTimesRoute}
                            onSelectItem={handleSelectItem}
                            onRimQuantityChange={handleRimQuantityChange}
                            largeCheckbox
                            isSelected={
                                selectedRimDetailsArticle?.artNr === x.artNr &&
                                selectedRimDetailsArticle?.appType === x.appType &&
                                selectedRimDetailsArticle?.idRim === x.idRim &&
                                selectedRimDetailsArticle?.applicationId === x.applicationId
                            }
                        />
                    ))}
                </Box>
            )
        }

        return <ScrollContainer sx={{ maxHeight: "22.5em" }}>{Object.entries(data).map(renderAxleCombinationArticle)}</ScrollContainer>
    }

    return (
        <StyledSimpleCard sxContent={{ padding: "0 0" }} sxCard={{ padding: "0 0.75em" }}>
            <Stack direction="row" gap={3} height="21em">
                <Stack direction="row" gap={2}>
                    <Stack direction="column" gap={1}>
                        {rimItem?.perspectivePicture && <StyledMainImage src={rimItem?.perspectivePicture} />}
                        {rimItem?.isWinterproofed && <Icon name="snow" size="large" sx={{ alignSelf: "end", marginTop: "-1em" }} color="black" />}
                        <Stack direction="row" gap={4}>
                            <Stack>
                                <UppercaseLabel variant="body2">{translateText(71)}</UppercaseLabel>
                                <UppercaseLabel variant="h2" color={theme?.colors?.primary} fontWeight={600}>
                                    {rimItem?.suplierName}
                                </UppercaseLabel>
                            </Stack>
                            <Stack>
                                <UppercaseLabel variant="body2">{translateText(14079)}</UppercaseLabel>
                                <UppercaseLabel variant="h2">{rimItem?.modelCode}</UppercaseLabel>
                            </Stack>
                        </Stack>
                        <Stack>
                            <UppercaseLabel variant="body2">{translateText(944)}</UppercaseLabel>
                            <Typography variant="h3">{rimItem?.color}</Typography>
                        </Stack>
                    </Stack>

                    <Divider orientation="vertical" sx={{ maxHeight: "16em" }} />
                </Stack>

                <Stack direction="row" gap={3} sx={{ width: "100%" }}>
                    <Stack gap={0.5} padding="1em 0">
                        {rimItem?.sizeInfo.map(renderRimSize)}
                    </Stack>
                    {displayListV2 ? <RimArticleListV2 /> : renderAxleCombinationArticles()}
                </Stack>
            </Stack>
        </StyledSimpleCard>
    )
}
